if (!window.customElements.get("personalization-examples")) {
  class PersonalizationExamples extends HTMLElement {
    constructor() {
      super();
      this.modal = null;
      this.modalOpener = null;
      this.modalCloser = null;
      this.swiperOptions = {};
      this.swiperJSUrl = null;
      this.swiperCSSUrl = null;
      this.blockId = "";
      this.zoomMobile = "no_zoom";
      this.zoomDesktop = "no_zoom";
      this.zoomModalSelector = "";
      this.zoomMediaQuery = window.matchMedia("(min-width: 750px)");
      this.container = null;
      this.productInfo = null;
      this.easifyObserver = null;
      this.easifyUpdateFrame = null;

      this.slider = null;
      this.swiper = null;
      this.initialized = false;
    }

    getElements() {
      if (!this.container) {
        this.container = this.closest(".personalization-examples");
      }
      if (!this.productInfo) {
        this.productInfo = this.closest(".product__info-container") || this.closest("product-info");
      }
      if (!this.modal) {
        this.modal = this.querySelector(`#${this.blockId}-personalization-examples-modal`);
      }
      if (!this.modalOpener) {
        this.modalOpener = this.querySelector(`#${this.blockId}-personalization-examples-modal-opener`);
      }
      if (!this.slider) {
        this.slider = this.querySelector(".swiper");
      }
      if (!this.modalCloser) {
        this.modalCloser = this.querySelector(`#${this.blockId}-personalization-examples-modal-closer`);
      }
    }

    getAttributes() {
      if (!this.swiperJSUrl) {
        this.swiperJSUrl = this.getAttribute("data-swiper-js");
      }
      if (!this.swiperCSSUrl) {
        this.swiperCSSUrl = this.getAttribute("data-swiper-css");
      }

      const swiperOptions = this.getAttribute("data-swiper-options");

      try {
        this.swiperOptions = swiperOptions ? JSON.parse(swiperOptions) : {};
      } catch (error) {
        console.warn("Can't parse swiper options");
      }

      this.blockId = this.getAttribute("data-block-id") || "";
      this.zoomMobile = this.getAttribute("data-zoom-mobile") || "no_zoom";
      this.zoomDesktop = this.getAttribute("data-zoom-desktop") || "no_zoom";
      this.zoomModalSelector = this.getAttribute("data-zoom-modal") || "";
    }

    hasMeaningfulElement(element) {
      if (!element || ["SCRIPT", "STYLE", "TEMPLATE"].includes(element.tagName)) {
        return false;
      }

      if (element.textContent.trim().length > 0 || element.matches("input, select, textarea, button")) {
        return true;
      }

      return Array.from(element.children).some((child) => this.hasMeaningfulElement(child));
    }

    hasMeaningfulEasifyContent(easifyOptions) {
      if (!easifyOptions) {
        return false;
      }

      const optionContainers = Array.from(easifyOptions.querySelectorAll(".tpo_option-set-container"));

      if (optionContainers.length > 0) {
        return optionContainers.some((container) => this.hasMeaningfulElement(container));
      }

      return Array.from(easifyOptions.children).some((child) => this.hasMeaningfulElement(child));
    }

    hasEmptyEasifyPriceOnlyState(easifyOptions) {
      if (!easifyOptions) {
        return false;
      }

      return Array.from(easifyOptions.querySelectorAll(".tpo_option-set-wrapper")).some((wrapper) => {
        const totalPrice = wrapper.querySelector(".tpo_total-additional-price-wrapper");

        if (!totalPrice) {
          return false;
        }

        const optionContainers = Array.from(wrapper.querySelectorAll(".tpo_option-set-container"));

        return (
          optionContainers.length === 0 ||
          optionContainers.some((container) => container.children.length === 0 && container.textContent.trim() === "")
        );
      });
    }

    hasValidEasifyOptions() {
      const scope = this.productInfo || document;
      const easifyOptionsList = Array.from(scope.querySelectorAll(".easify-product-options"));

      return easifyOptionsList.some((easifyOptions) => {
        return (
          this.hasMeaningfulEasifyContent(easifyOptions) && !this.hasEmptyEasifyPriceOnlyState(easifyOptions)
        );
      });
    }

    updateVisibilityByEasifyOptions = () => {
      this.easifyUpdateFrame = null;

      if (!this.container) {
        return;
      }

      const shouldShow = this.hasValidEasifyOptions();
      this.container.hidden = !shouldShow;
    };

    scheduleEasifyVisibilityUpdate = () => {
      if (this.easifyUpdateFrame) {
        return;
      }

      this.easifyUpdateFrame = window.requestAnimationFrame(this.updateVisibilityByEasifyOptions);
    };

    watchEasifyOptions() {
      if (!this.container?.matches("[data-requires-easify-options]")) {
        return;
      }

      this.updateVisibilityByEasifyOptions();

      if (!this.productInfo || this.easifyObserver) {
        return;
      }

      this.easifyObserver = new MutationObserver(this.scheduleEasifyVisibilityUpdate);
      this.easifyObserver.observe(this.productInfo, {
        attributes: true,
        attributeFilter: ["class"],
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    handleShowModal = () => {
      if (this.isZoomEnabled() && this.zoomModalSelector) {
        const zoomModal = document.querySelector(this.zoomModalSelector);

        if (zoomModal) {
          zoomModal.show(this.modalOpener);
          return;
        }
      }

      this.initSwiper();
      this.modal?.showModal();
    };

    handleCloseModal = () => this.modal?.close();

    handleClickModal = (event) => {
      if (event.currentTarget === event.target) {
        this.modal?.close();
      }
    };

    addListeners() {
      if (this.modal) {
        this.modalOpener?.addEventListener("click", this.handleShowModal);
        this.modalCloser?.addEventListener("click", this.handleCloseModal);
        this.modal?.addEventListener("click", this.handleClickModal);
      }
    }

    connectedCallback() {
      this.getAttributes();
      this.getElements();
      this.watchEasifyOptions();
      this.addListeners();
    }

    disconnectedCallback() {
      if (this.easifyUpdateFrame) {
        window.cancelAnimationFrame(this.easifyUpdateFrame);
      }
      if (this.easifyObserver) {
        this.easifyObserver.disconnect();
      }
      if (this.modal) {
        this.modal.removeEventListener("click", this.handleClickModal);
      }
      if (this.modalOpener) {
        this.modalOpener.removeEventListener("click", this.handleShowModal);
      }
      if (this.modalCloser) {
        this.modalCloser.removeEventListener("click", this.handleCloseModal);
      }
      if (this.swiper) {
        this.swiper.destroy();
      }
    }

    isZoomEnabled() {
      if (this.zoomMediaQuery.matches) {
        return this.zoomDesktop === "enable_zoom";
      }

      return this.zoomMobile === "enable_zoom";
    }

    async initSwiper() {
      if (this.swiper || this.initialized || !this.slider) {
        return;
      }

      let isCSSLoaded = false;
      let isJSLoaded = false;

      try {
        [isCSSLoaded, isJSLoaded] = await this.loadSwiper();
      } catch (error) {
        console.warn("Can't load Swiper assets");
        return;
      }

      if (isCSSLoaded && isJSLoaded && window.Swiper) {
        const showPagination = this.swiperOptions.pagination ?? true;
        const showNavigationMobile = this.swiperOptions.navigationMobile ?? true;
        const showNavigationDesktop = this.swiperOptions.navigationDesktop ?? false;
        const hasNavigation = showNavigationMobile || showNavigationDesktop;

        const sliderOptions = {
          pagination: showPagination ? {el: this.querySelector(".swiper-pagination"), clickable: true} : false,
          navigation: hasNavigation
            ? {
                nextEl: ".swiper-button-next",
                prevEl: ".swiper-button-prev",
                enabled: showNavigationMobile,
              }
            : false,
          grabCursor: this.swiperOptions.grabCursor ?? true,
          slidesPerView: 1,
          breakpoints: {
            750: {
              navigation: {
                enabled: showNavigationDesktop,
              },
            },
          },
        };

        this.swiper = new window.Swiper(this.slider, sliderOptions);
        this.initialized = true;
      }
    }

    async loadSwiper() {
      if (window.Swiper) {
        return [true, true];
      }

      if (window.__personalizationExamplesSwiperPromise) {
        return window.__personalizationExamplesSwiperPromise;
      }

      if (this.swiperJSUrl && this.swiperCSSUrl) {
        window.__personalizationExamplesSwiperPromise = Promise.all([
          this.loadStylesheet(this.swiperCSSUrl),
          this.loadScript(this.swiperJSUrl),
        ]);

        return window.__personalizationExamplesSwiperPromise;
      }

      return [false, false];
    }

    loadStylesheet(href) {
      return new Promise((resolve) => {
        if (!href) {
          resolve(false);
          return;
        }

        const existingLink = document.querySelector(`link[href="${href}"]`);

        if (existingLink) {
          resolve(true);
          return;
        }

        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = href;
        link.onload = () => resolve(true);
        link.onerror = () => resolve(false);

        document.head.appendChild(link);
      });
    }

    loadScript(src) {
      return new Promise((resolve, reject) => {
        if (!src) {
          reject(new Error("Missing Swiper JS asset URL."));
          return;
        }

        const existingScript = document.querySelector(`script[src="${src}"]`);

        if (existingScript) {
          if (existingScript.dataset.loaded === "true") {
            resolve(true);
            return;
          }

          existingScript.addEventListener("load", () => resolve(true), {once: true});
          existingScript.addEventListener("error", () => reject(new Error("Swiper script has failed")), {once: true});
          return;
        }

        const script = document.createElement("script");
        script.src = src;
        script.async = true;

        script.addEventListener(
          "load",
          () => {
            script.dataset.loaded = "true";
            resolve(true);
          },
          {once: true},
        );

        script.addEventListener("error", () => reject(new Error("Swiper script has failed")), {once: true});

        document.head.appendChild(script);
      });
    }
  }

  window.customElements.define("personalization-examples", PersonalizationExamples);
}
